import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function HeroSlidesManagement() {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [editingSlide, setEditingSlide] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    background_image: '',
    interior_image: '',
    link: '',
    order: 0,
    active: true
  });

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async () => {
    try {
      const response = await api.get('/hero-slides');
      setSlides(response.data);
    } catch (error) {
      console.error('Failed to fetch slides:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (slide) => {
    setEditingSlide(slide.id);
    setFormData({
      title: slide.title,
      subtitle: slide.subtitle,
      background_image: slide.background_image,
      interior_image: slide.interior_image,
      link: slide.link || '',
      order: slide.order,
      active: slide.active
    });
    setShowDialog(true);
  };

  const handleCreate = () => {
    setEditingSlide(null);
    setFormData({
      title: '',
      subtitle: '',
      background_image: '',
      interior_image: '',
      link: '',
      order: slides.length,
      active: true
    });
    setShowDialog(true);
  };

  const handleBackgroundImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);
      
      const response = await api.post('/upload', formDataObj);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      setFormData(prev => ({
        ...prev,
        background_image: `${backendUrl}${response.data.url}`
      }));
    } catch (error) {
      console.error('Failed to upload background image:', error);
      alert('Failed to upload background image');
    } finally {
      setUploading(false);
    }
  };

  const handleInteriorImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);
      
      const response = await api.post('/upload', formDataObj);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      setFormData(prev => ({
        ...prev,
        interior_image: `${backendUrl}${response.data.url}`
      }));
    } catch (error) {
      console.error('Failed to upload interior image:', error);
      alert('Failed to upload interior image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingSlide) {
        await api.put(`/hero-slides/${editingSlide}`, formData);
      } else {
        await api.post('/hero-slides', formData);
      }
      fetchSlides();
      setShowDialog(false);
    } catch (error) {
      console.error('Failed to save slide:', error);
      alert('Failed to save slide');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await api.delete(`/hero-slides/${deleteId}`);
      setSlides(slides.filter(s => s.id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      console.error('Failed to delete slide:', error);
      alert('Failed to delete slide');
    }
  };

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-testid="hero-slides-title">Hero Slides</h1>
          <p className="text-muted-foreground">Manage homepage carousel slides</p>
        </div>
        <Button onClick={handleCreate} data-testid="add-slide-button">
          <Plus className="mr-2 h-4 w-4" />
          Add Slide
        </Button>
      </div>

      {/* Slides List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-lg text-gray-500">Loading slides...</div>
        </div>
      ) : slides.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ImageIcon className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No slides found</h3>
            <p className="text-gray-500 mb-4">Get started by adding your first hero slide</p>
            <Button onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Add Slide
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="slides-grid">
          {slides.map((slide) => (
            <Card key={slide.id} data-testid={`slide-card-${slide.id}`}>
              <div className="aspect-video bg-gray-200 relative overflow-hidden grid grid-cols-2">
                {/* Background/Exterior Image - Left Side */}
                {slide.background_image ? (
                  <img
                    src={slide.background_image}
                    alt={`${slide.title} - Exterior`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full bg-gray-100">
                    <ImageIcon className="h-8 w-8 text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">Exterior</span>
                  </div>
                )}
                
                {/* Interior Image - Right Side */}
                {slide.interior_image ? (
                  <img
                    src={slide.interior_image}
                    alt={`${slide.title} - Interior`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full bg-gray-50">
                    <ImageIcon className="h-8 w-8 text-gray-400 mb-1" />
                    <span className="text-xs text-gray-500">Interior</span>
                  </div>
                )}
                
                <div className="absolute top-2 right-2">
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                    slide.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                  }`}>
                    {slide.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              <CardHeader>
                <CardTitle className="text-lg">{slide.title}</CardTitle>
                <p className="text-sm text-gray-600">{slide.subtitle}</p>
                <div className="text-xs text-gray-500 mt-2">Order: {slide.order}</div>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleEdit(slide)} className="flex-1">
                    <Edit className="mr-2 h-3 w-3" />
                    Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 text-red-600 hover:text-red-700"
                    onClick={() => setDeleteId(slide.id)}
                  >
                    <Trash2 className="mr-2 h-3 w-3" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingSlide ? 'Edit Slide' : 'Add New Slide'}</DialogTitle>
            <DialogDescription>Fill in the slide details</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  required
                  data-testid="slide-title-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="subtitle">Subtitle *</Label>
                <Input
                  id="subtitle"
                  value={formData.subtitle}
                  onChange={(e) => setFormData(prev => ({ ...prev, subtitle: e.target.value }))}
                  required
                  data-testid="slide-subtitle-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="link">Link (optional)</Label>
                <Input
                  id="link"
                  value={formData.link}
                  onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                  placeholder="https://..."
                  data-testid="slide-link-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="order">Display Order *</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) }))}
                  required
                  data-testid="slide-order-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Background Image (Exterior) *</Label>
                <p className="text-xs text-gray-500">This image will show on the left side of the carousel</p>
                {formData.background_image ? (
                  <div className="relative inline-block w-full">
                    <img
                      src={formData.background_image}
                      alt="Background"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, background_image: '' }))}
                      className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-2"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <div className="text-center">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="mt-4">
                        <label htmlFor="background-image-upload" className="cursor-pointer">
                          <span className="mt-2 block text-sm font-medium text-gray-900">
                            Upload background image
                          </span>
                          <input
                            id="background-image-upload"
                            type="file"
                            accept="image/*"
                            onChange={handleBackgroundImageUpload}
                            className="hidden"
                            disabled={uploading}
                            data-testid="slide-background-image-upload"
                          />
                        </label>
                      </div>
                      {uploading && (
                        <div className="mt-4 flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="text-sm">Uploading...</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Interior Image *</Label>
                <p className="text-xs text-gray-500">This image will show on the right side with angled clip</p>
                {formData.interior_image ? (
                  <div className="relative inline-block w-full">
                    <img
                      src={formData.interior_image}
                      alt="Interior"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, interior_image: '' }))}
                      className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-2"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <div className="text-center">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="mt-4">
                        <label htmlFor="interior-image-upload" className="cursor-pointer">
                          <span className="mt-2 block text-sm font-medium text-gray-900">
                            Upload interior image
                          </span>
                          <input
                            id="interior-image-upload"
                            type="file"
                            accept="image/*"
                            onChange={handleInteriorImageUpload}
                            className="hidden"
                            disabled={uploading}
                            data-testid="slide-interior-image-upload"
                          />
                        </label>
                      </div>
                      {uploading && (
                        <div className="mt-4 flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="text-sm">Uploading...</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="active"
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, active: checked }))}
                  data-testid="slide-active-switch"
                />
                <Label htmlFor="active">Active</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!formData.background_image || !formData.interior_image} data-testid="slide-submit-button">
                {editingSlide ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the slide.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
